import { memo } from "react";
import styles from "./confirmation-modal.module.scss";

interface ConfirmationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	title: string;
	message: string;
	confirmText?: string;
	cancelText?: string;
	isLoading?: boolean;
	loadingText?: string;
	variant?: "danger" | "warning" | "info";
}

export const ConfirmationModal = memo(({
	isOpen,
	onClose,
	onConfirm,
	title,
	message,
	confirmText = "Confirm",
	cancelText = "Cancel",
	isLoading = false,
	loadingText,
	variant = "danger"
}: ConfirmationModalProps) => {
	if (!isOpen) return null;

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const getVariantClass = () => {
		switch (variant) {
			case "danger":
				return styles.danger;
			case "warning":
				return styles.warning;
			case "info":
				return styles.info;
			default:
				return styles.danger;
		}
	};

	return (
		<div className={styles.backdrop} onClick={handleBackdropClick}>
			<div className={styles.modal}>
				<div className={styles.header}>
					<h2 className={styles.title}>{title}</h2>
					<button
						className={styles.closeButton}
						onClick={onClose}
						disabled={isLoading}
					>
						×
					</button>
				</div>
				<div className={styles.body}>
					<p className={styles.message}>{message}</p>
				</div>
				<div className={styles.footer}>
					<button
						className={`${styles.button} ${styles.cancelButton}`}
						onClick={onClose}
						disabled={isLoading}
					>
						{cancelText}
					</button>
					<button
						className={`${styles.button} ${styles.confirmButton} ${getVariantClass()}`}
						onClick={onConfirm}
						disabled={isLoading}
					>
						{isLoading ? (loadingText ?? `${confirmText}...`) : confirmText}
					</button>
				</div>
			</div>
		</div>
	);
});
